/**
 * The theme picker in every header: an icon button showing the current choice, and a menu
 * naming all three.
 *
 * A menu rather than a button that cycles through the three: under `system`, one of `light`
 * and `dark` always looks identical to it, so a cycling button has a click that visibly does
 * nothing. Naming each choice, with a tick on the current one, has no such dead step.
 */
import { Monitor, Moon, Sun } from 'lucide-react'
import { Button } from './ui/button'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuGroup,
	DropdownMenuLabel,
	DropdownMenuRadioGroup,
	DropdownMenuRadioItem,
	DropdownMenuTrigger,
} from './ui/dropdown-menu'
import { cn } from '../lib/utils'
import { isTheme, type Theme, useTheme } from '../lib/theme'

const CHOICES = [
	{ value: 'light', label: 'Light', Icon: Sun },
	{ value: 'dark', label: 'Dark', Icon: Moon },
	{ value: 'system', label: 'System', Icon: Monitor },
] as const satisfies readonly { value: Theme; label: string; Icon: typeof Sun }[]

export function ThemeMenu({ className }: { className?: string }) {
	const { theme, setTheme } = useTheme()
	const current = CHOICES.find((choice) => choice.value === theme) ?? CHOICES[2]

	return (
		<DropdownMenu>
			<DropdownMenuTrigger
				render={
					<Button
						aria-label={`Theme: ${current.label}`}
						title={`Theme: ${current.label}`}
						className={cn('text-ink-2 hover:text-ink', className)}
						size="icon-sm"
						variant="ghost"
					/>
				}
			>
				<current.Icon />
			</DropdownMenuTrigger>
			<DropdownMenuContent align="end" side="bottom" className="min-w-36">
				<DropdownMenuGroup>
					<DropdownMenuLabel className="text-xs text-ink-3">Theme</DropdownMenuLabel>
					<DropdownMenuRadioGroup
						value={theme}
						onValueChange={(value: unknown) => {
							if (isTheme(value)) setTheme(value)
						}}
					>
						{CHOICES.map(({ value, label, Icon }) => (
							<DropdownMenuRadioItem key={value} value={value}>
								<Icon />
								{label}
							</DropdownMenuRadioItem>
						))}
					</DropdownMenuRadioGroup>
				</DropdownMenuGroup>
			</DropdownMenuContent>
		</DropdownMenu>
	)
}
